/**
 * Records the input stream of the run currently being played.
 *
 * One `record()` per fixed simulation step, called from the same place the
 * step is taken - if the recorder and the simulation could ever disagree about
 * which inputs a step saw, the replay would be fiction.
 *
 * Recording is deliberately cheap: three numbers pushed onto an array. The
 * encoding happens once, at the finish line.
 */

import type { StepInput } from '../sim/StepInput';
import { BUTTON_MASK, clampPitchUnits, wrapYawUnits } from '../sim/StepInput';
import { MAX_REPLAY_FRAMES, encodeReplay, type Replay, type ReplayHeader } from './ReplayFormat';

export class ReplayRecorder {
  private frames: StepInput[] = [];
  private header: ReplayHeader | null = null;
  private overflowed = false;

  /** Starts a fresh recording. Called on every restart, not just the first. */
  begin(header: ReplayHeader): void {
    this.header = header;
    this.frames = [];
    this.overflowed = false;
  }

  get frameCount(): number {
    return this.frames.length;
  }

  get isRecording(): boolean {
    return this.header !== null && !this.overflowed;
  }

  /**
   * True once the run has gone on so long that the recording was cut off. Such
   * a run can still be played, it just cannot be submitted - and at 20 minutes
   * it was never going to be a leaderboard time anyway.
   */
  get isOverflowed(): boolean {
    return this.overflowed;
  }

  record(input: StepInput): void {
    if (this.header === null || this.overflowed) return;
    if (this.frames.length >= MAX_REPLAY_FRAMES) {
      this.overflowed = true;
      return;
    }
    this.frames.push({
      buttons: input.buttons & BUTTON_MASK,
      yawQ: wrapYawUnits(input.yawQ),
      pitchQ: clampPitchUnits(input.pitchQ),
    });
  }

  /** The recording as it stands. Null before `begin()` or after an overflow. */
  snapshot(): Replay | null {
    if (this.header === null || this.overflowed) return null;
    return { header: this.header, frames: [...this.frames] };
  }

  encode(): Uint8Array | null {
    const replay = this.snapshot();
    return replay ? encodeReplay(replay) : null;
  }
}
